import { ReactNode, createContext, useContext, useMemo, useState } from 'react';

// The time a visitor proposes in the hero travels with them to the brief form.
export interface ProposedSlot {
	day: Date | null;
	window: string | null;
}

interface BookingContextProps {
	slot: ProposedSlot;
	setSlot: (slot: ProposedSlot) => void;
	// Index into the brief form's topic list, set by the CTA the visitor came from.
	topic: number;
	setTopic: (topic: number) => void;
}

const BookingContext = createContext<BookingContextProps>({
	slot: { day: null, window: null },
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setSlot: () => {},
	topic: 0,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setTopic: () => {},
});

export function BookingProvider({ children }: { children: ReactNode }) {
	const [slot, setSlot] = useState<ProposedSlot>({ day: null, window: null });
	const [topic, setTopic] = useState(0);
	const value = useMemo(() => ({ slot, setSlot, topic, setTopic }), [slot, topic]);
	return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export const useBooking = () => useContext(BookingContext);

// The next working days, starting tomorrow.
export function upcomingWorkdays(count: number, from = new Date()) {
	const days: Date[] = [];
	const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
	while (days.length < count) {
		cursor.setDate(cursor.getDate() + 1);
		const weekday = cursor.getDay();
		if (weekday !== 0 && weekday !== 6) days.push(new Date(cursor));
	}
	return days;
}

export function formatSlot(slot: ProposedSlot, locale: string, compact = false) {
	if (!slot.day) return '';
	const day = slot.day.toLocaleDateString(locale, compact ? { weekday: 'short', day: 'numeric', month: 'long' } : { weekday: 'long', day: 'numeric', month: 'long' });
	return slot.window ? `${day}, ${slot.window}` : day;
}
